/**
 * Bulk Upload — Column Auto-Matching
 *
 * Automatically matches spreadsheet column headers to schema field definitions
 * using header aliases and normalized string comparison.
 */

import {
	ColumnMapping,
	UploadFieldDef,
	TemplateCompatibility,
	TemplateMappingEntry,
} from './types';
import { normalize } from './resolve';

// ---------------------------------------------------------------------------
// Auto-Match Algorithm
// ---------------------------------------------------------------------------

/**
 * Auto-match spreadsheet headers to schema fields using a 3-tier strategy:
 * 1. Exact normalized match against field name, label, or aliases
 * 2. Containment match (one contains the other)
 * 3. Hidden metadata row match (__field:fieldName)
 *
 * Each header is used at most once (first-come-first-served by field priority).
 */
export function autoMatchColumns(headers: string[], fields: UploadFieldDef[]): ColumnMapping[] {
	const unmatchedHeaders = new Set(headers);
	const mappings: ColumnMapping[] = [];

	for (const field of fields) {
		const aliases = [field.name, field.label, ...(field.headerAliases ?? [])];
		const normalizedAliases = aliases.map(normalize);

		let match: string | undefined;

		// Tier 1: Exact normalized match
		match = headers.find(
			(h) => unmatchedHeaders.has(h) && normalizedAliases.includes(normalize(h))
		);

		// Tier 1b: Check for hidden metadata row marker (__field:fieldName)
		if (!match) {
			match = headers.find(
				(h) => unmatchedHeaders.has(h) && normalize(h) === normalize(`__field:${field.name}`)
			);
		}

		// Tier 2: Containment (one contains the other after normalization)
		// Both strings must be ≥ 5 chars to avoid false matches from short words
		if (!match) {
			match = headers.find((h) => {
				if (!unmatchedHeaders.has(h)) return false;
				const hn = normalize(h);
				return normalizedAliases.some(
					(a) =>
						(hn.length >= 5 && a.length >= 5 && a.includes(hn)) ||
						(a.length >= 5 && hn.length >= 5 && hn.includes(a))
				);
			});
		}

		mappings.push({
			targetField: field.name,
			sourceColumn: match ?? null,
			autoMatched: !!match,
		});

		if (match) {
			unmatchedHeaders.delete(match);
		}
	}

	return mappings;
}

// ---------------------------------------------------------------------------
// Template Compatibility Check
// ---------------------------------------------------------------------------

/**
 * Check how well a saved template's column mappings match the uploaded file's headers.
 * Returns full-match, partial-match, or incompatible.
 */
export function checkTemplateCompatibility(
	templateMappings: TemplateMappingEntry[],
	fileHeaders: string[]
): TemplateCompatibility {
	const headerNormSet = new Map<string, string>();
	for (const h of fileHeaders) {
		headerNormSet.set(normalize(h), h);
	}

	const matched: ColumnMapping[] = [];
	const missing: ColumnMapping[] = [];
	const usedHeaders = new Set<string>();

	for (const mapping of templateMappings) {
		const normSource = normalize(mapping.sourceColumn);
		const actualHeader = headerNormSet.get(normSource);

		if (actualHeader && !usedHeaders.has(actualHeader)) {
			matched.push({
				targetField: mapping.targetField,
				sourceColumn: actualHeader,
				autoMatched: true,
			});
			usedHeaders.add(actualHeader);
		} else {
			missing.push({
				targetField: mapping.targetField,
				sourceColumn: null,
				autoMatched: false,
			});
		}
	}

	const unmapped = fileHeaders.filter((h) => !usedHeaders.has(h));

	return {
		status:
			missing.length === 0 ? 'full-match' : matched.length > 0 ? 'partial-match' : 'incompatible',
		matched,
		missing,
		unmapped,
	};
}

/**
 * Apply a saved template's mappings to produce a ColumnMapping array.
 * Falls back to auto-match for any template mappings that don't match the file.
 */
export function applyTemplate(
	templateMappings: TemplateMappingEntry[],
	fileHeaders: string[],
	fields: UploadFieldDef[]
): ColumnMapping[] {
	const compat = checkTemplateCompatibility(templateMappings, fileHeaders);

	// Start with matched mappings from the template
	const result = new Map<string, ColumnMapping>();
	for (const m of compat.matched) {
		result.set(m.targetField, m);
	}

	// For missing mappings, try auto-match on remaining headers
	const usedHeaders = new Set(
		compat.matched.map((m) => m.sourceColumn).filter(Boolean) as string[]
	);
	const availableHeaders = fileHeaders.filter((h) => !usedHeaders.has(h));

	for (const m of compat.missing) {
		const field = fields.find((f) => f.name === m.targetField);
		if (!field) {
			result.set(m.targetField, m);
			continue;
		}

		const aliases = [field.name, field.label, ...(field.headerAliases ?? [])];
		const normalizedAliases = aliases.map(normalize);
		const match = availableHeaders.find((h) => normalizedAliases.includes(normalize(h)));

		result.set(m.targetField, {
			targetField: m.targetField,
			sourceColumn: match ?? null,
			autoMatched: !!match,
		});

		if (match) {
			availableHeaders.splice(availableHeaders.indexOf(match), 1);
		}
	}

	// Ensure all schema fields have a mapping entry
	for (const field of fields) {
		if (!result.has(field.name)) {
			result.set(field.name, {
				targetField: field.name,
				sourceColumn: null,
				autoMatched: false,
			});
		}
	}

	return fields
		.map((f) => result.get(f.name) ?? null)
		.filter((v): v is NonNullable<typeof v> => v !== null);
}

/**
 * Get the count/stats of a column matching result for display.
 */
export function getMatchStats(mappings: ColumnMapping[], fields: UploadFieldDef[]) {
	const total = fields.length;
	const matched = mappings.filter((m) => m.sourceColumn !== null).length;
	const requiredFields = fields.filter((f) => f.required);
	const requiredMatched = requiredFields.filter((rf) =>
		mappings.some((m) => m.targetField === rf.name && m.sourceColumn !== null)
	).length;
	const requiredMissing = requiredFields.length - requiredMatched;

	return { total, matched, requiredTotal: requiredFields.length, requiredMatched, requiredMissing };
}
